import { useEffect, useState, useMemo } from "react";
import { Search, ChevronDown } from "lucide-react";
import { client } from "../lib/config";
import SEOHead, { useSiteBase, faqPageSchema, breadcrumbSchema } from "../lib/SEOHead";
import PageHero from "../components/PageHero";
import Breadcrumbs from "../components/Breadcrumbs";
import BookCallCTA from "../components/BookCallCTA";

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [query, setQuery] = useState("");
  const [openIds, setOpenIds] = useState(new Set());
  const { base } = useSiteBase();

  useEffect(() => {
    client.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
    client.get("/faqs/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = faqs.filter((f) => {
      if (activeCat !== "all" && f.category !== activeCat) return false;
      if (!q) return true;
      return f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q);
    });
    const g = {};
    filtered.forEach((f) => {
      g[f.category] = g[f.category] || [];
      g[f.category].push(f);
    });
    return g;
  }, [faqs, activeCat, query]);

  const toggle = (id) => {
    setOpenIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  };

  return (
    <>
      <PageHero
        eyebrow="FAQ"
        title="Questions we hear on the phone."
        intro="Booking, rooms, breakfast, pets, boat and truck parking, drive times, Truman Lake, the Katy Trail — the direct-answer version. If you don't see your question, call the desk at (660) 885-2206."
        image="/images/lobby-2.jpg"
        metaTitle="FAQ — Booking, Rooms, Pets, Parking, Location"
        metaDesc="Frequently asked questions about Westbridge Inn & Suites in Clinton, MO — booking, rooms, breakfast, pets, boat & truck parking, drive times to Truman Lake and Katy Trail."
      />
      <SEOHead
        title="FAQ — Booking, Rooms, Pets, Parking, Location"
        description="Frequently asked questions about Westbridge Inn & Suites in Clinton, MO — booking, rooms, breakfast, pets, boat & truck parking, drive times to Truman Lake and Katy Trail."
        canonicalPath="/faq"
        schema={faqs.length > 0 ? [faqPageSchema(faqs), breadcrumbSchema(base, [{ label: "FAQ", to: "/faq" }])] : []}
      />
      <Breadcrumbs items={[{ label: "FAQ" }]} />

      <section className="bg-[#FDFBF7]">
        <div className="wb-container py-12 md:py-16">
          {/* Search + filter */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A4641]" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search FAQs…"
                className="w-full pl-10 pr-4 py-3 border border-[#E5DFD3] rounded-sm bg-white text-[#1A1A1A] focus:border-[#E85D26] focus:outline-none focus:ring-2 focus:ring-[#E85D26]/20"
                data-testid="faq-search-input"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-10" data-testid="faq-category-filters">
            <button
              onClick={() => setActiveCat("all")}
              data-testid="faq-cat-all"
              className={`px-4 py-2 text-sm font-bold rounded-sm border ${
                activeCat === "all" ? "bg-[#E85D26] text-white border-[#E85D26]" : "bg-white border-[#E5DFD3] text-[#1A1A1A] hover:border-[#E85D26]"
              }`}
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCat(c)}
                data-testid={`faq-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-4 py-2 text-sm font-bold rounded-sm border ${
                  activeCat === c ? "bg-[#E85D26] text-white border-[#E85D26]" : "bg-white border-[#E5DFD3] text-[#1A1A1A] hover:border-[#E85D26]"
                }`}
                style={{ fontFamily: "'Nunito', sans-serif" }}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Groups */}
          <div className="space-y-10">
            {Object.keys(grouped).length === 0 && (
              <p className="text-center py-16 text-[#4A4641]">No FAQs match your search.</p>
            )}
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <h2 className="wb-h3 mb-4" data-testid={`faq-group-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <span className="wb-divider inline-block mr-3 align-middle" />
                  {cat}
                </h2>
                <div className="space-y-3">
                  {items.map((f) => {
                    const isOpen = openIds.has(f.id);
                    return (
                      <div key={f.id} className="wb-card bg-white" data-testid={`faq-item-${f.id}`}>
                        <button
                          onClick={() => toggle(f.id)}
                          className="w-full flex items-center justify-between gap-4 p-5 text-left"
                          aria-expanded={isOpen}
                          data-testid={`faq-toggle-${f.id}`}
                        >
                          <span className="font-bold text-[#1A1A1A]" style={{ fontFamily: "'Nunito', sans-serif" }}>
                            {f.question}
                          </span>
                          <ChevronDown className={`h-5 w-5 text-[#B94A15] transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`} />
                        </button>
                        {isOpen && (
                          <div className="px-5 pb-5 text-[#4A4641] wb-body text-base" data-testid={`faq-answer-${f.id}`}>
                            {f.answer}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BookCallCTA />
    </>
  );
}
